import React, { useState } from 'react';
import { styled, Box, Typography, Collapse, Switch } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { FormData, PermissionGroupItem } from '../types';
import Spacing from 'components/Spacing';

type PermissionGroupBoxProps = {
  title: string;
  description: string;
  items: Array<PermissionGroupItem>;
  defaultExpanded?: boolean;
  form: UseFormReturn<FormData>;
};

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  border: `1px solid ${theme.palette.borderColor.dark}`,
  overflow: 'hidden',
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.md),
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
}));

const HeaderDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.fontSizes.regular,
  color: '#666666',
  lineHeight: 1.5,
}));

const CaretIcon = styled(Box)({
  color: '#4caf50',
  display: 'flex',
  alignItems: 'center',
  marginLeft: '16px',
});

const Divider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.palette.borderColor.main,
}));

const PermissionItemsContainer = styled(Box)(({ theme }) => ({
  padding: `0 ${theme.spacing(theme.spacingSizes.sm)}`,
}));

const PermissionItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: '#F8F8F8',
  },
}));

const PermissionLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.darkBodyText,
}));

const PermissionDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.fontSizes.sm,
  color: '#928C8C',
  lineHeight: 1.5,
}));

const PermissionGroupBox: React.FC<PermissionGroupBoxProps> = ({
  title,
  description,
  items,
  defaultExpanded = true,
  form,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const allSelectedPermissions = form.watch('permissions');

  const isItemChecked = (item: PermissionGroupItem) => {
    if (isDisabled(item)) {
      return false;
    }

    return item.permissions.every((permission) =>
      allSelectedPermissions.includes(permission),
    );
  };

  const onChange = (item: PermissionGroupItem) => () => {
    if (isDisabled(item)) {
      return;
    }

    if (
      item.permissions.every((permission) =>
        allSelectedPermissions.includes(permission),
      )
    ) {
      form.setValue(
        'permissions',
        allSelectedPermissions.filter((p) => !item.permissions.includes(p)),
      );
    } else {
      form.setValue('permissions', [
        ...allSelectedPermissions,
        ...item.permissions,
      ]);
    }
  };

  const isDisabled = (item: PermissionGroupItem) =>
    item.permissions.length === 0;

  return (
    <Card>
      <Header
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <Spacing size="xs">
          <Typography variant="h4">{title}</Typography>
          <HeaderDescription>{description}</HeaderDescription>
        </Spacing>
        <CaretIcon>
          {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </CaretIcon>
      </Header>
      <Collapse in={expanded}>
        <PermissionItemsContainer>
          <Divider />
          {items.map((item, index) => (
            <React.Fragment key={item.label}>
              <PermissionItem onClick={onChange(item)}>
                <Spacing size="xs">
                  <PermissionLabel>{item.label}</PermissionLabel>
                  {item.description ? (
                    <PermissionDescription>
                      {item.description}
                    </PermissionDescription>
                  ) : null}
                </Spacing>

                <Switch
                  checked={isItemChecked(item)}
                  onChange={onChange(item)}
                  disabled={isDisabled(item)}
                />
              </PermissionItem>
              {index < items.length - 1 ? <Divider /> : null}
            </React.Fragment>
          ))}
        </PermissionItemsContainer>
      </Collapse>
    </Card>
  );
};

export default PermissionGroupBox;
